import type { AxiosProgressEvent, AxiosRequestConfig } from "axios";
import { ApiEndpoints } from "../apiEndpoints";
import { requestApi } from "../apiService";
import { PostAttachmentPayload } from "../types/attachment";
import {
  UPLOAD_STALL_TIMEOUT_MS,
  UPLOAD_RESPONSE_TIMEOUT_MS,
} from "@/config/constants";

/**
 * Thrown when postAttachment aborts an upload that made no progress. Typed so the
 * caller's catch can tell a stall from any other failure — requestApi launders
 * axios's CanceledError into a bare Error, so the message alone is not reliable.
 */
export class UploadTimeoutError extends Error {
  readonly phase: "transfer" | "response";
  constructor(phase: "transfer" | "response") {
    super(`Attachment upload timed out (${phase} phase)`);
    this.name = "UploadTimeoutError";
    this.phase = phase;
  }
}

/**
 * POST an encrypted attachment as a single octet-stream body, with a
 * progress-based timeout so a stalled upload fails instead of hanging forever.
 *
 * Two phases, because XHR emits NO progress events once loaded === total:
 *  - transfer: reset a 60s idle timer on every progress event; a real stall
 *    (no bytes for 60s) aborts.
 *  - response: once the body is fully sent, switch to a separate, longer
 *    deadline (the server may still be writing a large body). Using the idle
 *    timer here would abort a request the server is about to complete → an
 *    attachment stored server-side but marked FAILED client-side (orphan).
 *    Note this shifts, but does not eliminate, that orphan risk: a genuine
 *    server write-through slower than UPLOAD_RESPONSE_TIMEOUT_MS still aborts.
 *
 * 401 token refresh: the response interceptor transparently refreshes and
 * REPLAYS this request. During that window no progress fires, so the armed timer
 * would keep counting against a request that is not in flight — `onBeforeRetry`
 * re-arms a fresh transfer window the moment the replay re-fires (the retry then
 * re-arms normally from its own progress events). Residual: a refresh that itself
 * takes longer than the armed window can still abort before the replay begins.
 * The timer is cleared only in `finally`.
 */
export const postAttachment = async ({
  attachment_id,
  filename,
  chat_id,
  encryptedAttachment,
}: PostAttachmentPayload): Promise<string> => {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let timedOut = false;
  let timedOutPhase: "transfer" | "response" = "transfer";

  const arm = (ms: number, phase: "transfer" | "response") => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      timedOut = true;
      timedOutPhase = phase;
      controller.abort();
    }, ms);
  };

  arm(UPLOAD_STALL_TIMEOUT_MS, "transfer");

  // Custom `onBeforeRetry` hook — the 401 interceptor calls it just before it
  // replays this request (see apiService response interceptor).
  const requestConfig: AxiosRequestConfig & { onBeforeRetry?: () => void } = {
    url: ApiEndpoints.ATTACHMENTS,
    method: "POST",
    headers: { "Content-Type": "application/octet-stream" },
    params: {
      attachment_id,
      filename,
      chat_id,
    },
    data: encryptedAttachment,
    signal: controller.signal,
    onUploadProgress: (e: AxiosProgressEvent) => {
      const done = e.total != null && e.loaded >= e.total;
      arm(
        done ? UPLOAD_RESPONSE_TIMEOUT_MS : UPLOAD_STALL_TIMEOUT_MS,
        done ? "response" : "transfer",
      );
    },
    onBeforeRetry: () => arm(UPLOAD_STALL_TIMEOUT_MS, "transfer"),
  };

  try {
    const response = await requestApi(requestConfig);

    return response.data;
  } catch (error) {
    if (timedOut) {
      throw new UploadTimeoutError(timedOutPhase);
    }
    throw error;
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
};
